import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { useInteractionStore } from '../../stores/interactionStore';
import { useMediaStore, type PeerMedia } from '../../stores/mediaStore';
import { localMedia } from '../../webrtc/localMedia';
import { spatialAudio } from '../../webrtc/spatialAudio';
import { useOffice } from '../office/OfficeContext';
import './av.css';

/**
 * Peers eines aktiven Gespraechs — an der Medien-Freigabe (`mediaActive`)
 * festgemacht, NICHT am bereits empfangenen Stream. Sonst oeffnet sich die
 * Leiste nur auf der Seite, die etwas empfaengt (Gegenseite ohne Kamera →
 * gar keine Leiste). Kacheln ohne Stream zeigen einen Platzhalter.
 */
function useCallPeers(): PeerMedia[] {
  const peers = useMediaStore((s) => s.peers);
  return Object.values(peers).filter((p) => p.mediaActive);
}

/**
 * Gespraechs-Leiste am oberen Rand (Gather-Style): eine Reihe statischer
 * Kacheln, die die Karte NICHT ueberdeckt, sondern als Flow-Element den
 * Buehnenbereich darunter verkleinert. Oeffnet sich automatisch beim ersten
 * Medien-Peer; „Auflegen" beendet das Gespraech, „Minimieren" gibt die Karte frei.
 */
export function CallStrip() {
  const inCall = useCallPeers();
  const open = useMediaStore((s) => s.callPanelOpen);
  const setLocal = useMediaStore((s) => s.setLocal);
  const zone = useInteractionStore((s) => s.currentZone);
  const { socket } = useOffice();

  // Automatisch oeffnen, wenn ein Gespraech beginnt (0 -> >=1 Teilnehmer)
  const hadCall = useRef(false);
  useEffect(() => {
    const hasCall = inCall.length > 0;
    if (hasCall && !hadCall.current) setLocal({ callPanelOpen: true });
    hadCall.current = hasCall;
  }, [inCall.length, setLocal]);

  if (inCall.length === 0 || !open) return null;

  // In Raum-Konferenz-Zonen kann man nicht „auflegen" — man verlaesst den Raum
  const inZoneCall = zone?.audio === 'zone';

  return (
    <section className="call-strip" aria-label="Gespraech">
      <div className="call-strip__tiles">
        {inCall.map((peer) => (
          <CallTile key={peer.peerId} peer={peer} />
        ))}
        <SelfTile />
      </div>
      <div className="call-strip__actions">
        {!inZoneCall && (
          <button
            className="call-strip__hangup"
            onClick={() => socket.emit('call:end', () => undefined)}
            title="Gespräch beenden"
          >
            📞 Auflegen
          </button>
        )}
        <button
          className="call-strip__minimize"
          onClick={() => setLocal({ callPanelOpen: false })}
          title="Leiste minimieren"
        >
          Minimieren
        </button>
      </div>
    </section>
  );
}

/** Kleiner Chip zum Wiederaufklappen, wenn die Leiste minimiert ist. */
export function CallStripChip() {
  const inCall = useCallPeers();
  const open = useMediaStore((s) => s.callPanelOpen);
  const setLocal = useMediaStore((s) => s.setLocal);
  if (inCall.length === 0 || open) return null;
  return (
    <button className="call-panel-chip" onClick={() => setLocal({ callPanelOpen: true })}>
      📞 Gespräch anzeigen ({inCall.length})
    </button>
  );
}

function CallTile({ peer }: { peer: PeerMedia }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [enlargedScreen, setEnlargedScreen] = useState(false);
  const bigVideoRef = useRef<HTMLVideoElement>(null);
  // Screen hat Prioritaet in der Kachel (mehr Inhalt), sonst Kamera
  const stream = peer.streams.screen ?? peer.streams.cam ?? null;
  const isScreen = !!peer.streams.screen;

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  useEffect(() => {
    if (enlargedScreen && bigVideoRef.current) bigVideoRef.current.srcObject = peer.streams.screen ?? null;
  }, [enlargedScreen, peer.streams.screen]);

  return (
    <div className="call-tile">
      {stream ? (
        // Audio kommt aus dem Spatial-Audio-Graph — Video bleibt stumm
        <video ref={videoRef} className="call-tile__video" autoPlay playsInline muted />
      ) : (
        <div className="call-tile__placeholder">🎙️</div>
      )}
      <span className="call-tile__label">
        {isScreen ? '🖥️ ' : ''}
        {peer.displayName}
      </span>
      {isScreen && (
        <button className="call-tile__enlarge" onClick={() => setEnlargedScreen(true)} title="Vergrößern">
          ⤢
        </button>
      )}
      {enlargedScreen && (
        <Modal title={`Bildschirm von ${peer.displayName}`} onClose={() => setEnlargedScreen(false)} wide>
          <video ref={bigVideoRef} className="screen-window__video--big" autoPlay playsInline muted />
        </Modal>
      )}
    </div>
  );
}

/**
 * Eigene Kachel — immer sichtbar, solange man im Gespraech ist. Ohne
 * freigegebene Kamera/Mikrofon erscheint ein Hinweis mit Freigabe-Buttons
 * (Freigabe laeuft in der User-Geste, wie im HudBar).
 */
function SelfTile() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const camLive = useMediaStore((s) => s.camLive);
  const camArmed = useMediaStore((s) => s.camArmed);
  const micArmed = useMediaStore((s) => s.micArmed);
  const mediaError = useMediaStore((s) => s.mediaError);
  const setLocal = useMediaStore((s) => s.setLocal);

  useEffect(() => {
    if (camLive && videoRef.current) videoRef.current.srcObject = localMedia.getStream('cam');
  }, [camLive]);

  const share = async (kind: 'cam' | 'mic'): Promise<void> => {
    spatialAudio.resume();
    setLocal({ mediaError: null });
    try {
      await localMedia.setArmed(kind, true);
    } catch {
      setLocal({ mediaError: 'Zugriff auf Kamera/Mikrofon verweigert' });
    }
  };

  if (camLive) {
    return (
      <div className="call-tile call-tile--self" title="Deine Kamera">
        <video ref={videoRef} className="call-tile__video call-tile__video--self" autoPlay playsInline muted />
        <span className="call-tile__label">Du{micArmed ? '' : ' · 🔇'}</span>
      </div>
    );
  }

  return (
    <div className="call-tile call-tile--self call-tile--hint">
      <div className="call-tile__hint">
        <p className="call-tile__hint-text">
          {mediaError ?? (micArmed ? 'Kamera nicht freigegeben' : 'Du teilst weder Kamera noch Mikrofon')}
        </p>
        <div className="call-tile__hint-actions">
          {!camArmed && (
            <button onClick={() => void share('cam')} title="Kamera freigeben">📷 Kamera</button>
          )}
          {!micArmed && (
            <button onClick={() => void share('mic')} title="Mikrofon freigeben">🎙️ Mikro</button>
          )}
        </div>
      </div>
      <span className="call-tile__label">Du</span>
    </div>
  );
}
